import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One-time onboarding nudge that asks new portfolio owners to set
// their MAGI + filing status so the NIIT exposure tile (and the Dec
// banner) actually do something useful. Gates:
//   1. Authenticated
//   2. User has at least one portfolio position
//   3. niit_prefs.filing_status is null (i.e., never set)
//   4. Not dismissed permanently (`niit_onboarding_dismissed`)
//
// Renders as a soft violet card — visually distinct from the urgent
// amber December banner so users don't conflate the two.

export default function NiitOnboardingNudge({ hasPositions = false }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!hasPositions) return;
    let cancelled = false;
    (async () => {
      try {
        const [prefsRes, dismissRes] = await Promise.all([
          axios.get(`${API}/users/me/niit-prefs`, { withCredentials: true }),
          axios.get(`${API}/users/me/dashboard-dismissals`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        const prefs = prefsRes.data || {};
        const dismissals = (dismissRes.data || {}).dismissals || {};
        if (dismissals.niit_onboarding_dismissed) return;
        // Already set → user has gone through onboarding; don't nag
        if (prefs.filing_status && typeof prefs.magi === 'number') return;
        setShow(true);
      } catch {
        // anon / unauth — silently skip
      }
    })();
    return () => { cancelled = true; };
  }, [hasPositions]);

  const handleDismiss = async () => {
    setShow(false);
    try {
      await axios.post(
        `${API}/users/me/dashboard-dismissals`,
        { key: 'niit_onboarding_dismissed', value: true },
        { withCredentials: true },
      );
    } catch {
      // ignore — banner is already hidden in this session
    }
  };

  if (!show) return null;

  return (
    <div
      className="relative rounded-xl border border-violet-300/60 dark:border-violet-700/40 bg-gradient-to-r from-violet-50/70 via-purple-50/50 to-violet-50/70 dark:from-violet-950/30 dark:via-purple-950/25 dark:to-violet-950/30 p-3 mb-4"
      data-testid="niit-onboarding-nudge"
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        title="Dismiss permanently"
        className="absolute top-2 right-2 p-1 rounded-md hover:bg-violet-200/50 dark:hover:bg-violet-800/30 text-violet-700 dark:text-violet-300"
        data-testid="niit-onboarding-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="shrink-0 w-7 h-7 rounded-full bg-violet-200/70 dark:bg-violet-800/40 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-violet-700 dark:text-violet-300" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-violet-900 dark:text-violet-100">
            Unlock your personal <AbbrTip term="NIIT">NIIT</AbbrTip> exposure tile
          </p>
          <p className="text-[11px] text-violet-800 dark:text-violet-200/90 mt-0.5 leading-snug">
            Add your <AbbrTip term="MAGI"><strong>MAGI</strong></AbbrTip> + <strong>filing status</strong> on the Year-End Report
            and we&rsquo;ll show how much of your projected dividends face the 3.8%
            surtax. Takes 10 seconds, only visible to you.
          </p>
          <Link
            to="/year-end-report"
            className="inline-flex items-center gap-1 px-2.5 py-1 mt-2 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-bold shadow-sm transition-colors"
            data-testid="niit-onboarding-cta"
          >
            Set it up <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
